// Our base component, which all others extend.
import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './pc-grid.css.lit';
import { styleMap } from 'lit/directives/style-map.js';
import { ResizeController } from '../../../controllers/resize-controller';
import globalStyles from './pc-grid.lightDom.css.lit';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';

/**
 * The grid component
 * @element pc-grid
 *
 * @slot - The default, and only slot for this element.
 *
 * @attr row-gap
 * @attr col-gap
 * @attr columns
 * @attr item-min-width
 * @attr align-self
 * @attr justify-self
 * @attr equal-height
 * @attr span-first
 */
@customElement('pc-grid')
export class PCGrid extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  resizeController: ResizeController;
  adoptedStylesheets: AdoptedStyleSheets;

  // The resizeController is initialized here because attributes are not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();

    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);
    if (this.spanFirst) {
      // Calculate the minimum width of two columns in pixels
      const minWidthOf2Columns =
        this.convertRemToPx(this.itemMinWidth) * 2 +
        this.convertRemToPx(this.colGap);

      // Initialize the ResizeController with the minimum width of two columns as a breakpoint
      this.resizeController = new ResizeController(this, {
        breakpoints: [minWidthOf2Columns - 1],
      });
    }
  }

  convertRemToPx(item: string) {
    if (item.endsWith('rem')) {
      return parseFloat(item) * 16;
    } else {
      return parseInt(item, 10);
    }
  }

  /**
   * Vertical gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'row-gap',
  })
  rowGap = '1rem';

  /**
   * Horizontal gap between items.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'col-gap',
  })
  colGap = '1rem';

  /**
   * Maximum amount of columns.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'columns',
  })
  columns = '3';

  /**
   * Maximum amount of columns.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'span-first',
  })
  spanFirst: boolean;

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'item-min-width',
  })
  itemMinWidth = '200px';

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'align-self',
  })
  alignSelf = '';

  /**
   * Minimum width of each item
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'justify-self',
  })
  justifySelf = '';

  /**
   * Whether or not all rows will have equal height.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-height',
  })
  equalHeight = false;

  /**
   * Sets all rows to equal height only when there is more than one column .
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'equal-at-plus',
  })
  equalAtPlus = false;

  /**
   * Whether or not all rows will have equal height.
   */
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'mobile-carousel',
  })
  mobileCarousel: boolean;

  /**
   * Sets/removes the `span-two-columns` class on the element (:host) if the component's width is less than 2 columns.
   */
  spanFirstColumn() {
    const minWidthOf2Columns = this.resizeController.currentBreakpointRange > 0;
    if (minWidthOf2Columns) {
      this.classList.add('span-two-columns');
    } else {
      this.classList.remove('span-two-columns');
    }
  }

  render(): TemplateResult {
    if (this.spanFirst) {
      this.spanFirstColumn();
    }

    const gridStyles = {
      '--grid-layout-column-gap': this.colGap,
      '--grid-layout-row-gap': this.rowGap,
      '--grid-column--max-count': this.columns,
      '--grid-item--min-width': this.itemMinWidth,
      '--align-self': this.alignSelf || 'auto',
      '--justify-self': this.justifySelf || 'auto',
    };
    return html` <div
      class="grid-wrapper
        ${this.alignSelf ? 'align-self' : ''}
        ${this.justifySelf ? 'justify-self' : ''}
        ${this.equalHeight ? 'equal-height' : ''}
        ${this.equalAtPlus ? 'equal-at-plus' : ''}
        ${this.mobileCarousel ? 'mobile-carousel' : ''}
      "
      style=${styleMap(gridStyles)}
    >
      <slot></slot>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-grid': PCGrid;
  }
}
